'use client';

import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SidebarStoreEntry } from '@/components/layout/sidebar-nav-premium';
import { SidebarMenu } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Store } from '@/lib/api/stores';
import { cn } from '@/lib/utils';

type StoreRoutes = ReturnType<
  typeof import('@/config/router').STORE_ROUTES
>;

type StoreLabels = {
  general: string;
  storefront: string;
  emails: string;
  paymentFlows: string;
  integrations: string;
  storeSettings: string;
};

const orderStoresWithCurrentFirst = (stores: Store[], storeId: string | null) => {
  if (!storeId) return stores;
  const current = stores.find((store) => store.id === storeId);
  if (!current) return stores;
  return [current, ...stores.filter((store) => store.id !== storeId)];
};

type SidebarStoresListProps = {
  stores: Store[];
  storeId: string | null;
  storeSearch: string;
  isLoading: boolean;
  displayName: (name: string) => string;
  storeRoutesFor: (id: string) => StoreRoutes;
  labels: StoreLabels;
  activeStoreItemRef?: React.Ref<HTMLLIElement>;
};

export function SidebarStoresList({
  stores,
  storeId,
  storeSearch,
  isLoading,
  displayName,
  storeRoutesFor,
  labels,
  activeStoreItemRef,
}: SidebarStoresListProps) {
  const { t } = useTranslation('navigation');
  const isMobile = useIsMobile();
  const [othersOpen, setOthersOpen] = React.useState(false);

  React.useEffect(() => {
    setOthersOpen(false);
  }, [storeId]);

  const orderedStores = React.useMemo(
    () => orderStoresWithCurrentFirst(stores, storeId),
    [stores, storeId],
  );

  const collapseOthers = isMobile && !!storeId && !storeSearch.trim();
  const primaryStores = collapseOthers
    ? orderedStores.filter((store) => store.id === storeId)
    : orderedStores;
  const secondaryStores = collapseOthers
    ? orderedStores.filter((store) => store.id !== storeId)
    : [];

  const renderStore = (store: Store) => (
    <SidebarStoreEntry
      key={store.id}
      ref={store.id === storeId ? activeStoreItemRef : undefined}
      store={store}
      storeRoutes={storeRoutesFor(store.id)}
      isCurrentStore={store.id === storeId}
      displayName={displayName(store.name)}
      labels={labels}
    />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <SidebarMenu className="gap-px pb-1">
      {primaryStores.map(renderStore)}
      {secondaryStores.length > 0 ? (
        <li className="list-none px-0.5 pt-1">
          <button
            type="button"
            className={cn('crm-sidebar-other-stores-toggle w-full')}
            onClick={() => setOthersOpen((open) => !open)}
            aria-expanded={othersOpen}
          >
            {othersOpen
              ? t('hideOtherStores')
              : t('otherStores', { count: secondaryStores.length })}
          </button>
          {othersOpen ? secondaryStores.map(renderStore) : null}
        </li>
      ) : null}
    </SidebarMenu>
  );
}
